// SPDX-License-Identifier: AGPL-3.0-or-later

import {Routes} from '@app/app/Routes';
import styles from '@app/features/app/components/layout/pioneer/PioneerSidebar.module.css';
import {ChannelCreateModal} from '@app/features/channel/components/modals/ChannelCreateModal';
import * as RouterUtils from '@app/features/navigation/utils/RouterUtils';
import Permission from '@app/features/permissions/state/Permission';
import {
	CHATS_LABEL_DESCRIPTOR,
	NEW_CHANNEL_DESCRIPTOR,
	NEW_CHAT_DESCRIPTOR,
	NEW_MESSAGE_DESCRIPTOR,
} from '@app/features/tasks/utils/TaskMessageDescriptors';
import * as ModalCommands from '@app/features/ui/commands/ModalCommands';
import {modal} from '@app/features/ui/commands/ModalCommands';
import {Permissions} from '@fluxer/constants/src/ChannelConstants';
import {useLingui} from '@lingui/react/macro';
import {ChatCircleIcon, HashIcon, PlusIcon} from '@phosphor-icons/react';
import {observer} from 'mobx-react-lite';
import {useEffect, useId, useRef, useState} from 'react';

/** + button that starts a channel (text or voice) or a direct message. */
export const NewChatMenu = observer(({guildId, className}: {guildId: string; className?: string}) => {
	const {i18n} = useLingui();
	const [open, setOpen] = useState(false);
	const rootRef = useRef<HTMLDivElement | null>(null);
	const menuId = useId();
	const canCreateChannel = Permission.can(Permissions.MANAGE_CHANNELS, {guildId});

	useEffect(() => {
		if (!open) return;
		const onPointerDown = (event: PointerEvent) => {
			if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
		};
		const onKeyDown = (event: KeyboardEvent) => {
			if (event.key === 'Escape') setOpen(false);
		};
		document.addEventListener('pointerdown', onPointerDown);
		document.addEventListener('keydown', onKeyDown);
		return () => {
			document.removeEventListener('pointerdown', onPointerDown);
			document.removeEventListener('keydown', onKeyDown);
		};
	}, [open]);

	const newChannel = () => {
		setOpen(false);
		ModalCommands.push(
			modal(() => (
				<ChannelCreateModal guildId={guildId} data-flx="app.pioneer.chats-heading.new-channel.channel-create-modal" />
			)),
		);
	};
	const newMessage = () => {
		setOpen(false);
		RouterUtils.transitionTo(Routes.ME);
	};

	return (
		<div className={styles.newChat} ref={rootRef} data-flx="app.pioneer.chats-heading.new-chat">
			<button
				type="button"
				className={className ?? styles.labelAction}
				aria-label={i18n._(NEW_CHAT_DESCRIPTOR)}
				aria-expanded={open}
				aria-controls={menuId}
				onClick={() => setOpen((value) => !value)}
				data-flx="app.pioneer.chats-heading.label-action.set-open.button"
			>
				<PlusIcon size={14} aria-hidden="true" data-flx="app.pioneer.chats-heading.plus-icon" />
			</button>
			{open && (
				<div className={styles.chatsMenu} id={menuId} data-flx="app.pioneer.chats-heading.chats-menu">
					{canCreateChannel && (
						<button
							type="button"
							className={styles.downloadItem}
							onClick={newChannel}
							data-flx="app.pioneer.chats-heading.download-item.new-channel.button"
						>
							<HashIcon size={16} aria-hidden="true" data-flx="app.pioneer.chats-heading.hash-icon" />
							{i18n._(NEW_CHANNEL_DESCRIPTOR)}
						</button>
					)}
					<button
						type="button"
						className={styles.downloadItem}
						onClick={newMessage}
						data-flx="app.pioneer.chats-heading.download-item.new-message.button"
					>
						<ChatCircleIcon size={16} aria-hidden="true" data-flx="app.pioneer.chats-heading.chat-circle-icon" />
						{i18n._(NEW_MESSAGE_DESCRIPTOR)}
					</button>
				</div>
			)}
		</div>
	);
});

/** The "Chats" heading in the sidebar, with the + menu. */
export const ChatsHeading = observer(({guildId}: {guildId: string}) => {
	const {i18n} = useLingui();
	return (
		<div className={styles.chatsHeading} data-flx="app.pioneer.chats-heading.chats-heading">
			<span data-flx="app.pioneer.chats-heading.span">{i18n._(CHATS_LABEL_DESCRIPTOR)}</span>
			<NewChatMenu guildId={guildId} data-flx="app.pioneer.chats-heading.new-chat-menu" />
		</div>
	);
});
